import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';

import { NavigationItem } from '../types';
import { DesktopMenuItem } from './DesktopMenuItem';

type DesktopMenuProps = {
  navigationItems: NavigationItem[];
};

// Grace period before a mega-menu closes on mouse-out. It lets the pointer
// travel the gap between a (possibly wrapped, two-row) nav item and its
// mega-menu without the menu vanishing mid-move — re-entering either cancels
// the pending close. Without it, a wrapped nav makes the sub-nav unreachable.
const CLOSE_DELAY_MS = 200;

export const DesktopMenu = ({ navigationItems }: DesktopMenuProps) => {
  // Each item carries its own stable, unique, DOM-safe id (index-prefixed, no
  // whitespace) so duplicate CMS labels can't collide — it doubles as the
  // MegaMenu DOM id referenced by aria-controls, and DesktopMenu is its single
  // source. Computing it alongside the item (not via a separate lookup) means
  // there is no fallback path that could silently re-derive a colliding id.
  const navItems = useMemo(
    () =>
      navigationItems.map((item, index) => ({
        item,
        id: `${index}-${item.mainLink.value.replace(/\s+/g, '-')}`,
        isRightAligned: Boolean(item.isRightAligned),
      })),
    [navigationItems]
  );
  const leftItems = navItems.filter((entry) => !entry.isRightAligned);
  const rightItems = navItems.filter((entry) => entry.isRightAligned);

  const [activeId, setActiveId] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const open = (id: string) => {
    clearCloseTimer();
    setActiveId(id);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setActiveId(null), CLOSE_DELAY_MS);
  };

  const closeNow = () => {
    clearCloseTimer();
    setActiveId(null);
  };

  // Pointer tracking is delegated to the whole <ul>: mouseover (which bubbles
  // up from the items and the open flyout) keeps the active item in sync as the
  // pointer moves, and a single mouseleave on the list is the ONLY thing that
  // schedules a close. Every internal gap — the ml-auto space between the left
  // and right groups, item padding, the wrapped-row gap — lives inside the list,
  // so crossing one can never null the active item while the pointer is still in
  // the nav. (Per-item mouseleave used to fire in those gaps and drop the menu.)
  const handlePointerOver = (event: MouseEvent<HTMLUListElement>) => {
    const listItem = (event.target as HTMLElement).closest<HTMLElement>(
      'li[data-nav-id]'
    );
    if (listItem?.dataset.navId) {
      open(listItem.dataset.navId);
    } else {
      // over the flyout or an in-list gap — still inside the nav, keep it open
      clearCloseTimer();
    }
  };

  const handlePointerLeave = () => {
    // a keyboard user may have opened a menu and be tabbing through it; don't
    // close while focus is inside the ACTIVE item's li. Focus parked on a
    // different item must not pin this menu open — that item's blur handler is
    // inactive and would never close it.
    const listItems =
      listRef.current?.querySelectorAll<HTMLElement>('li[data-nav-id]');
    const activeListItem = Array.from(listItems ?? []).find(
      (li) => li.dataset.navId === activeId
    );
    if (activeListItem?.contains(document.activeElement)) {
      return;
    }
    scheduleClose();
  };

  useEffect(() => clearCloseTimer, []);

  // Single writer for the shared underline — per-item effects raced when
  // switching items and could hide the newly-active item's underline.
  useEffect(() => {
    const indicator = document.querySelector<HTMLElement>('#active-indicator');
    if (!indicator) {
      return;
    }
    const listItems =
      listRef.current?.querySelectorAll<HTMLElement>('li[data-nav-id]');
    const listItemElement = activeId
      ? Array.from(listItems ?? []).find((li) => li.dataset.navId === activeId)
      : undefined;
    if (!listItemElement) {
      indicator.style.opacity = '0';
      return;
    }
    // inset by the anchor's own horizontal padding so the underline spans the label
    const anchorElement = listItemElement.querySelector('a');
    const paddingLeft = anchorElement
      ? Number.parseFloat(getComputedStyle(anchorElement).paddingLeft)
      : NaN;
    const inset = Number.isNaN(paddingLeft) ? 16 : paddingLeft;
    indicator.style.left = `${listItemElement.offsetLeft + inset + 1}px`;
    indicator.style.width = `${listItemElement.offsetWidth - inset * 2}px`;
    indicator.style.opacity = '1';
  }, [activeId]);

  const renderItem = (
    { item, id }: { item: NavigationItem; id: string },
    className = ''
  ) => (
    <DesktopMenuItem
      key={id}
      id={id}
      item={item}
      className={className}
      isActive={activeId === id}
      onOpen={() => open(id)}
      onCloseNow={closeNow}
    />
  );

  return (
    <>
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/mouse-events-have-key-events --
          Hover is a pointer-only enhancement here; the mega-menu is fully
          keyboard-operable via each item's caret button (aria-expanded) + Escape.
          The pointer handlers must live on the <ul> (delegated) so an in-nav gap
          between items can't fire a per-item mouseleave and drop the active item. */}
      <ul
        ref={listRef}
        className="flex"
        onMouseOver={handlePointerOver}
        onMouseLeave={handlePointerLeave}
      >
        {leftItems.map((entry) => renderItem(entry))}
        {rightItems.map((entry, index) =>
          renderItem(entry, index === 0 ? 'ml-auto' : '')
        )}
      </ul>
      {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/mouse-events-have-key-events */}
      <div
        id="active-indicator"
        className="pointer-events-none absolute bottom-0 left-0 h-0.5 w-0 bg-blue-dark transition-all ease-out"
      />
    </>
  );
};
